import { createEntityAdapter, createSlice, createAsyncThunk, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { httpClient } from 'src/libs';
import { RootState } from 'src/store';
import { enqueueSnackbar } from 'notistack';

export interface Chunk {
  id: string;
  content: string;
  chunk_type?: string;
  chunkable_type?: string;
  chunkable_id?: string;
  account_id?: string;
  privacy_status?: 'private' | 'public';
  group_id?: string;
  status?: 'pending_generation' | 'active' | 'failed_generation' | 'pending_admin_approval' | 'invalid';
  raw_content?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  expires_at?: string;
  index_id?: string;
  similarity?: number; // only present in search results
}

export interface ChunkSearchResult {
  success: boolean;
  current_page: number;
  total_pages: number;
  total_count: number;
  has_more: boolean;
  chunks: Chunk[];
}

const chunksAdapter = createEntityAdapter<Chunk>({
  selectId: (chunk) => chunk.id,
  sortComparer: (a, b) => {
    // sort by created_at descending (newest first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  },
});

interface ChunksState extends EntityState<Chunk> {
  loading: boolean;
  searchLoading: boolean;
  error: any;
  searchError: any;
  totalChunks: number;
  hasMore: boolean;
  currentPage: number;
  perPage: number;
  selectedChunkId: string | null;
  searchResults: Chunk[];
  searchTotalCount: number;
  searchHasMore: boolean;
  searchCurrentPage: number;
  filters: {
    chunk_type?: string;
    status?: string;
    privacy_status?: string;
    group?: string;
  };
}

const initialState: ChunksState = chunksAdapter.getInitialState({
  loading: false,
  searchLoading: false,
  error: null,
  searchError: null,
  totalChunks: 0,
  hasMore: false,
  currentPage: 1,
  perPage: 25,
  selectedChunkId: null,
  searchResults: [],
  searchTotalCount: 0,
  searchHasMore: false,
  searchCurrentPage: 1,
  filters: {},
});

interface FetchChunksParams {
  chunk_type?: string;
  status?: string;
  privacy_status?: string;
  group?: string;
  page?: number;
  per_page?: number;
}

interface CreateChunkParams {
  content: string;
  chunk_type: string;
  chunkable_type?: string;
  chunkable_id?: string;
  privacy_status?: 'private' | 'public';
  group_id?: number;
  metadata?: Record<string, any>;
  expires_at?: string;
  status?: 'pending_generation' | 'active' | 'failed_generation' | 'pending_admin_approval' | 'invalid';
}

interface UpdateChunkParams {
  id: string;
  content?: string;
  chunk_type?: string;
  privacy_status?: 'private' | 'public';
  group_id?: number;
  metadata?: Record<string, any>;
  expires_at?: string;
}

interface PatchChunkParams {
  id: string;
  content?: string;
  chunk_type?: string;
  privacy_status?: 'private' | 'public';
  group_id?: number;
  metadata?: Record<string, any>;
  expires_at?: string;
  status?: 'pending_generation' | 'active' | 'failed_generation' | 'pending_admin_approval' | 'invalid';
}

interface SearchSimilarChunksParams {
  query: string;
  limit?: number;
  threshold?: number;
  distance?: 'cosine' | 'euclidean';
  chunk_type?: string;
  chunkable_type?: string;
  group_id?: string;
  account_id?: string;
  metadata?: Record<string, any>;
  created_after?: string;
  created_before?: string;
  updated_after?: string;
  updated_before?: string;
  order_by?: 'similarity' | 'created_at' | 'updated_at';
  order_direction?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
  privacy_status?: 'public' | 'private';
}

interface SearchPublicChunksParams {
  query: string;
  limit?: number;
  threshold?: number;
  distance?: 'cosine' | 'euclidean';
  chunk_type?: string;
  chunkable_type?: string;
  group_id?: string;
  metadata?: Record<string, any>;
  created_after?: string;
  created_before?: string;
  updated_after?: string;
  updated_before?: string;
  order_by?: 'similarity' | 'created_at' | 'updated_at';
  order_direction?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

interface SearchChunksInIndexParams {
  index_id: string;
  query: string;
  include_irrelevant?: boolean;
  limit?: number;
  threshold?: number;
  distance?: 'cosine' | 'euclidean';
  chunk_type?: string;
  chunkable_type?: string;
  group_id?: string;
  metadata?: Record<string, any>;
  created_after?: string;
  created_before?: string;
  updated_after?: string;
  updated_before?: string;
  order_by?: 'similarity' | 'created_at' | 'updated_at';
  order_direction?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

interface CreateRuleParams {
  situation: string;
  rule: string;
  example: string;
  index_id?: string;
  rule_type?: string;
  status?: string;
}

export const fetchChunks = createAsyncThunk(
  'chunks/fetchChunks',
  async (params: FetchChunksParams = {}) => {
    const searchParams = new URLSearchParams();
    
    if (params.chunk_type) searchParams.append('chunk_type', params.chunk_type);
    if (params.status) searchParams.append('status', params.status);
    if (params.privacy_status) searchParams.append('privacy_status', params.privacy_status);
    if (params.group) searchParams.append('group', params.group);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.per_page) searchParams.append('per_page', params.per_page.toString());

    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/v1/chunks?${searchParams.toString()}`;
    console.log('Fetching chunks from:', url);
    
    try {
      const response = await httpClient.get(url);
      console.log('Chunks response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching chunks:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  }
);

export const fetchChunkById = createAsyncThunk(
  'chunks/fetchChunkById',
  async (id: string) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/v1/chunks/${id}`;
    const response = await httpClient.get(url);
    return response.data;
  }
);

export const createChunk = createAsyncThunk(
  'chunks/createChunk',
  async (chunkData: CreateChunkParams, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/v1/chunks`;
      const response = await httpClient.post(url, chunkData);
      enqueueSnackbar('Chunk created successfully', { variant: 'success' });
      return response.data;
    } catch (error: any) {
      enqueueSnackbar('Error creating chunk', { variant: 'error' });
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateChunk = createAsyncThunk(
  'chunks/updateChunk',
  async ({ id, ...updateData }: UpdateChunkParams, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/v1/chunks/${id}`;
      const response = await httpClient.put(url, updateData);
      enqueueSnackbar('Chunk updated successfully', { variant: 'success' });
      return response.data;
    } catch (error: any) {
      enqueueSnackbar('Error updating chunk', { variant: 'error' });
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const patchChunk = createAsyncThunk(
  'chunks/patchChunk',
  async ({ id, ...patchData }: PatchChunkParams, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/v1/chunks/${id}`;
      const response = await httpClient.patch(url, patchData);
      enqueueSnackbar('Chunk updated successfully', { variant: 'success' });
      return response.data;
    } catch (error: any) {
      enqueueSnackbar('Error updating chunk', { variant: 'error' });
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteChunk = createAsyncThunk(
  'chunks/deleteChunk',
  async (id: string, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/v1/chunks/${id}`;
      await httpClient.delete(url);
      enqueueSnackbar('Chunk deleted successfully', { variant: 'success' });
      return id;
    } catch (error: any) {
      enqueueSnackbar('Error deleting chunk', { variant: 'error' });
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const searchSimilarChunks = createAsyncThunk(
  'chunks/searchSimilarChunks',
  async (params: SearchSimilarChunksParams, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      
      searchParams.append('query', params.query);
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.threshold) searchParams.append('threshold', params.threshold.toString());
      if (params.distance) searchParams.append('distance', params.distance);
      if (params.chunk_type) searchParams.append('chunk_type', params.chunk_type);
      if (params.chunkable_type) searchParams.append('chunkable_type', params.chunkable_type);
      if (params.group_id) searchParams.append('group_id', params.group_id);
      if (params.account_id) searchParams.append('account_id', params.account_id);
      if (params.metadata) searchParams.append('metadata', JSON.stringify(params.metadata));
      if (params.created_after) searchParams.append('created_after', params.created_after);
      if (params.created_before) searchParams.append('created_before', params.created_before);
      if (params.updated_after) searchParams.append('updated_after', params.updated_after);
      if (params.updated_before) searchParams.append('updated_before', params.updated_before);
      if (params.order_by) searchParams.append('order_by', params.order_by);
      if (params.order_direction) searchParams.append('order_direction', params.order_direction);
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.per_page) searchParams.append('per_page', params.per_page.toString());
      if (params.privacy_status) searchParams.append('privacy_status', params.privacy_status);

      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/v1/chunks/search/similar?${searchParams.toString()}`;
      const response = await httpClient.get(url);
      return response.data;
    } catch (error: any) {
      enqueueSnackbar('Error searching chunks', { variant: 'error' });
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const searchPublicChunks = createAsyncThunk(
  'chunks/searchPublicChunks',
  async (params: SearchPublicChunksParams, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      
      searchParams.append('query', params.query);
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.threshold) searchParams.append('threshold', params.threshold.toString());
      if (params.distance) searchParams.append('distance', params.distance);
      if (params.chunk_type) searchParams.append('chunk_type', params.chunk_type);
      if (params.chunkable_type) searchParams.append('chunkable_type', params.chunkable_type);
      if (params.group_id) searchParams.append('group_id', params.group_id);
      if (params.metadata) searchParams.append('metadata', JSON.stringify(params.metadata));
      if (params.created_after) searchParams.append('created_after', params.created_after);
      if (params.created_before) searchParams.append('created_before', params.created_before);
      if (params.updated_after) searchParams.append('updated_after', params.updated_after);
      if (params.updated_before) searchParams.append('updated_before', params.updated_before);
      if (params.order_by) searchParams.append('order_by', params.order_by);
      if (params.order_direction) searchParams.append('order_direction', params.order_direction);
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.per_page) searchParams.append('per_page', params.per_page.toString());

      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/v1/public/search?${searchParams.toString()}`;
      const response = await httpClient.get(url);
      return response.data;
    } catch (error: any) {
      enqueueSnackbar('Error searching public chunks', { variant: 'error' });
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const searchChunksInIndex = createAsyncThunk(
  'chunks/searchChunksInIndex',
  async (params: SearchChunksInIndexParams, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      
      searchParams.append('query', params.query);
      if (params.include_irrelevant) searchParams.append('include_irrelevant', params.include_irrelevant.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.threshold) searchParams.append('threshold', params.threshold.toString());
      if (params.distance) searchParams.append('distance', params.distance);
      if (params.chunk_type) searchParams.append('chunk_type', params.chunk_type);
      if (params.chunkable_type) searchParams.append('chunkable_type', params.chunkable_type);
      if (params.group_id) searchParams.append('group_id', params.group_id);
      if (params.metadata) searchParams.append('metadata', JSON.stringify(params.metadata));
      if (params.created_after) searchParams.append('created_after', params.created_after);
      if (params.created_before) searchParams.append('created_before', params.created_before);
      if (params.updated_after) searchParams.append('updated_after', params.updated_after);
      if (params.updated_before) searchParams.append('updated_before', params.updated_before);
      if (params.order_by) searchParams.append('order_by', params.order_by);
      if (params.order_direction) searchParams.append('order_direction', params.order_direction);
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.per_page) searchParams.append('per_page', params.per_page.toString());

      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/v1/public/index/search/${params.index_id}?${searchParams.toString()}`;
      const response = await httpClient.get(url);
      return response.data;
    } catch (error: any) {
      enqueueSnackbar('Error searching chunks in index', { variant: 'error' });
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createRuleViaNotesApi = createAsyncThunk(
  'chunks/createRuleViaNotesApi',
  async (ruleData: CreateRuleParams, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/rules`;
      const response = await httpClient.post(url, ruleData);
      enqueueSnackbar('Rule created successfully', { variant: 'success' });
      return response.data;
    } catch (error: any) {
      enqueueSnackbar('Error creating rule', { variant: 'error' });
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const chunksSlice = createSlice({
  name: 'chunks',
  initialState,
  reducers: {
    clearChunksState: (state) => {
      // save current selection
      const currentSelection = state.selectedChunkId;
      // reset to initial state
      Object.assign(state, initialState);
      // restore selection
      state.selectedChunkId = currentSelection;
    },
    setSelectedChunk: (state, action: PayloadAction<string | null>) => {
      state.selectedChunkId = action.payload;
    },
    setFilters: (state, action: PayloadAction<ChunksState['filters']>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPerPage: (state, action: PayloadAction<number>) => {
      state.perPage = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchTotalCount = 0;
      state.searchHasMore = false;
      state.searchCurrentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchChunks
      .addCase(fetchChunks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChunks.fulfilled, (state, action) => {
        state.loading = false;
        let chunks = Array.isArray(action.payload) ? action.payload : action.payload.chunks || [];
        
        // ensure each chunk has an id - use chunkable_id or generate one
        chunks = chunks.map((chunk: any) => ({
          ...chunk,
          id: chunk.id || chunk.chunkable_id || `${chunk.chunkable_type}-${chunk.chunkable_id}-${Date.now()}-${Math.random()}`
        }));
        
        // for paginated results, extract pagination info
        if (action.payload.total_count !== undefined) {
          state.totalChunks = action.payload.total_count;
          state.hasMore = action.payload.has_more || false;
          state.currentPage = action.payload.current_page || 1;
        }
        
        // if it's first page, replace all chunks
        if (state.currentPage === 1) {
          chunksAdapter.setAll(state, chunks);
        } else {
          // otherwise add new chunks
          chunksAdapter.addMany(state, chunks);
        }
      })
      .addCase(fetchChunks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // fetchChunkById
      .addCase(fetchChunkById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChunkById.fulfilled, (state, action) => {
        state.loading = false;
        chunksAdapter.upsertOne(state, action.payload);
      })
      .addCase(fetchChunkById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // createChunk
      .addCase(createChunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createChunk.fulfilled, (state, action) => {
        state.loading = false;
        chunksAdapter.addOne(state, action.payload);
      })
      .addCase(createChunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // createRuleViaNotesApi
      .addCase(createRuleViaNotesApi.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRuleViaNotesApi.fulfilled, (state, action) => {
        state.loading = false;
        // Add the chunk from the response (Notes API returns both note and chunk)
        if (action.payload.chunk) {
          chunksAdapter.addOne(state, action.payload.chunk);
        }
      })
      .addCase(createRuleViaNotesApi.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // updateChunk
      .addCase(updateChunk.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateChunk.fulfilled, (state, action) => {
        state.loading = false;
        chunksAdapter.upsertOne(state, action.payload);
      })
      .addCase(updateChunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // patchChunk
      .addCase(patchChunk.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(patchChunk.fulfilled, (state, action) => {
        state.loading = false;
        chunksAdapter.upsertOne(state, action.payload);
      })
      .addCase(patchChunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // deleteChunk
      .addCase(deleteChunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteChunk.fulfilled, (state, action) => {
        state.loading = false;
        chunksAdapter.removeOne(state, action.payload);
        // clear selection if deleted chunk was selected
        if (state.selectedChunkId === action.payload) {
          state.selectedChunkId = null;
        }
      })
      .addCase(deleteChunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // searchSimilarChunks
      .addCase(searchSimilarChunks.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchSimilarChunks.fulfilled, (state, action) => {
        state.searchLoading = false;
        const result = action.payload;
        state.searchResults = result.chunks || [];
        state.searchTotalCount = result.total_count || 0;
        state.searchHasMore = result.has_more || false;
        state.searchCurrentPage = result.current_page || 1;
      })
      .addCase(searchSimilarChunks.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload || action.error.message;
      })
      
      // searchPublicChunks
      .addCase(searchPublicChunks.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchPublicChunks.fulfilled, (state, action) => {
        state.searchLoading = false;
        const result = action.payload;
        state.searchResults = result.chunks || [];
        state.searchTotalCount = result.total_count || 0;
        state.searchHasMore = result.has_more || false;
        state.searchCurrentPage = result.current_page || 1;
      })
      .addCase(searchPublicChunks.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload || action.error.message;
      })
      
      // searchChunksInIndex
      .addCase(searchChunksInIndex.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchChunksInIndex.fulfilled, (state, action) => {
        state.searchLoading = false;
        const result = action.payload;
        state.searchResults = result.chunks || [];
        state.searchTotalCount = result.total_count || 0;
        state.searchHasMore = result.has_more || false;
        state.searchCurrentPage = result.current_page || 1;
      })
      .addCase(searchChunksInIndex.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload || action.error.message;
      });
  },
});

export const {
  selectAll: selectAllChunks,
  selectById: selectChunkById,
  selectIds: selectChunkIds,
} = chunksAdapter.getSelectors<RootState>((state) => state.chunks || chunksAdapter.getInitialState());

export const {
  clearChunksState,
  setSelectedChunk,
  setFilters,
  clearFilters,
  setPage,
  setPerPage,
  clearSearchResults,
} = chunksSlice.actions;

// selectors
export const selectChunksLoading = (state: RootState) => state.chunks.loading;
export const selectChunksSearchLoading = (state: RootState) => state.chunks.searchLoading;
export const selectChunksError = (state: RootState) => state.chunks.error;
export const selectChunksSearchError = (state: RootState) => state.chunks.searchError;
export const selectTotalChunks = (state: RootState) => state.chunks.totalChunks;
export const selectChunksHasMore = (state: RootState) => state.chunks.hasMore;
export const selectChunksCurrentPage = (state: RootState) => state.chunks.currentPage;
export const selectChunksPerPage = (state: RootState) => state.chunks.perPage;
export const selectSelectedChunkId = (state: RootState) => state.chunks.selectedChunkId;
export const selectChunksFilters = (state: RootState) => state.chunks.filters;
export const selectSearchResults = (state: RootState) => state.chunks.searchResults;
export const selectSearchTotalCount = (state: RootState) => state.chunks.searchTotalCount;
export const selectSearchHasMore = (state: RootState) => state.chunks.searchHasMore;
export const selectSearchCurrentPage = (state: RootState) => state.chunks.searchCurrentPage;

export const selectSelectedChunk = (state: RootState) => {
  const selectedId = state.chunks.selectedChunkId;
  return selectedId ? selectChunkById(state, selectedId) : null;
};

// filter chunks by status
export const selectChunksByStatus = (state: RootState, status: string) => 
  selectAllChunks(state).filter(chunk => chunk.status === status);

// filter chunks by type
export const selectChunksByType = (state: RootState, chunkType: string) => 
  selectAllChunks(state).filter(chunk => chunk.chunk_type === chunkType);

// filter chunks by privacy status
export const selectChunksByPrivacyStatus = (state: RootState, privacyStatus: 'private' | 'public') => 
  selectAllChunks(state).filter(chunk => chunk.privacy_status === privacyStatus);

export default chunksSlice.reducer; 